
import { useState } from "react";
import { format } from "date-fns";
import { Calendar, Eye, Flag, Pencil, Trash2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TaskDialog } from "./TaskDialog";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

type TaskStatus = "todo" | "in_progress" | "in_review" | "completed" | "blocked";
type TaskPriority = "low" | "medium" | "high";

interface Task {
  id: string;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  due_date?: string;
  assignee?: {
    id?: string;
    company_name?: string;
    email?: string;
  };
  creator?: {
    id?: string;
    company_name?: string;
    email?: string;
  };
  assignee_id?: string;
  creator_id: string;
}

interface TaskListProps {
  onTasksChange: () => void;
  tenderId: string;
  tasks: Task[];
}

export const TaskList = ({ onTasksChange, tenderId, tasks }: TaskListProps) => {
  const { toast } = useToast();
  const { t } = useTranslation('offerManagement');
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const queryClient = useQueryClient();

  const handleStatusChange = async (taskId: string, newStatus: TaskStatus) => {
    try {
      const { error } = await supabase
        .from("tender_tasks")
        .update({ status: newStatus })
        .eq("id", taskId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["tender-tasks", tenderId] });
      onTasksChange();
      toast({
        title: t("tasks.statusUpdated"),
        description: t("tasks.statusUpdatedDesc"),
      });
    } catch (error) {
      console.error("Error updating task status:", error);
      toast({
        title: t("errorMessages.generalError"),
        description: t("tasks.updateError"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      const { error } = await supabase
        .from("tender_tasks")
        .delete()
        .eq("id", taskId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["tender-tasks", tenderId] });
      onTasksChange();
      toast({
        title: t("tasks.taskDeleted"),
        description: t("tasks.taskDeletedDesc"),
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      toast({
        title: t("errorMessages.generalError"),
        description: t("tasks.deleteError"),
        variant: "destructive",
      });
    }
  };

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case "high":
        return "bg-red-500";
      case "medium":
        return "bg-yellow-500";
      case "low":
        return "bg-green-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case "completed":
        return "text-green-600";
      case "blocked":
        return "text-red-600";
      case "in_progress":
        return "text-blue-600";
      case "in_review":
        return "text-yellow-600";
      default:
        return "text-gray-600";
    }
  };

  // Sort tasks by priority (high -> medium -> low)
  const sortedTasks = [...tasks].sort((a, b) => {
    const priorityOrder = { high: 1, medium: 2, low: 3 };
    return priorityOrder[a.priority as keyof typeof priorityOrder] - 
           priorityOrder[b.priority as keyof typeof priorityOrder];
  });

  return (
    <div className="space-y-4">
      {sortedTasks.map((task) => {
        // Check if the assignee_id is "unassigned" and handle it appropriately
        const assigneeDisplay = task.assignee_id === "unassigned" ? 
          t("tasks.unassigned") : 
          (task.assignee ? (task.assignee.company_name || task.assignee.email) : t("tasks.unassigned"));
        
        return (
          <Card key={task.id} className="p-4">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h4 className="text-sm font-medium">{task.title}</h4>
                {task.description && (
                  <p className="text-sm text-muted-foreground line-clamp-1">
                    {task.description}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-4 bg-popover">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <h4 className="text-lg font-semibold">{task.title}</h4>
                        <div className="flex items-center space-x-2">
                          <Badge
                            variant="secondary"
                            className={getPriorityColor(task.priority)}
                          >
                            <Flag className="mr-1 h-3 w-3" />
                            {task.priority}
                          </Badge>
                          <Badge
                            variant="outline"
                            className={`${getStatusColor(task.status)} border-current`}
                          >
                            {task.status.replace("_", " ").toUpperCase()}
                          </Badge>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <h5 className="font-medium">{t("tasks.description")}</h5>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {task.description || t("tasks.noDescription")}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <h5 className="font-medium">{t("tasks.details")}</h5>
                        <div className="text-sm space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{t("tasks.assignedTo")}</span>
                            <span>{assigneeDisplay}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{t("tasks.createdBy")}</span>
                            <span>
                              {task.creator ? (task.creator.company_name || task.creator.email) : t("tasks.unknown")}
                            </span>
                          </div>
                          {task.due_date && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">{t("tasks.dueDate")}</span>
                              <span className="flex items-center">
                                <Calendar className="mr-1 h-3 w-3" />
                                {format(new Date(task.due_date), "PPP")}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditingTask(task)}
                  className="h-8 w-8"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-background">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-red-600" />
                        {t("tasks.deleteTask")}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("tasks.deleteConfirmation")}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("tasks.cancel")}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDeleteTask(task.id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {t("tasks.deleteTask")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Select
                  value={task.status}
                  onValueChange={(value: TaskStatus) =>
                    handleStatusChange(task.id, value)
                  }
                >
                  <SelectTrigger className="w-[140px] bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background">
                    <SelectItem value="todo">{t("tasks.filter.todo")}</SelectItem>
                    <SelectItem value="in_progress">{t("tasks.filter.inProgress")}</SelectItem>
                    <SelectItem value="in_review">{t("tasks.filter.inReview")}</SelectItem>
                    <SelectItem value="completed">{t("tasks.filter.completed")}</SelectItem>
                    <SelectItem value="blocked">{t("tasks.filter.blocked")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between text-sm">
              <div className="flex items-center space-x-4">
                <Badge variant="secondary" className={getPriorityColor(task.priority)}>
                  <Flag className="mr-1 h-3 w-3" />
                  {task.priority}
                </Badge>
                {task.due_date && (
                  <div className="flex items-center text-muted-foreground">
                    <Calendar className="mr-1 h-3 w-3" />
                    {format(new Date(task.due_date), "PPP")}
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2 text-muted-foreground">
                <span>
                  {t("tasks.assignedTo")}{" "}
                  {assigneeDisplay}
                </span>
              </div>
            </div>
          </Card>
        );
      })}

      {tasks.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          {t("tasks.noTasks")}
        </div>
      )}

      <TaskDialog
        tenderId={tenderId}
        open={!!editingTask}
        onOpenChange={(open) => !open && setEditingTask(null)}
        onSuccess={() => {
          onTasksChange();
          setEditingTask(null);
        }}
        task={editingTask}
      />
    </div>
  );
}
